import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, SkipForward, Check, Coins, ScrollText, Sparkles, Flame, Trophy, BookOpen, LayoutGrid, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TaskHistory from "@/components/TaskHistory";
import BlockTaskTypes from "@/components/BlockTaskTypes";
import { taskWikiUrl } from "@/lib/wiki";

const CAT_LABEL = {
  skilling: "Skilling", quests: "Quest", diaries: "Achievement Diary",
  combat: "Combat", raids: "Raid", collection: "Collection Log", clues: "Treasure Trail",
};

const TIER_COLOR = {
  Beginner: "bg-[#2a2a2a] text-[#d0d0d0]",
  Easy: "bg-[#1c3a1c] text-[#9be89b]",
  Medium: "bg-[#2b3352] text-[#9bb6ff]",
  Hard: "bg-[#4a2a10] text-[#ffb26b]",
  Elite: "bg-[#3a1c3a] text-[#e79bff]",
  Master: "bg-[#4a1414] text-[#ff8f8f]",
  Grandmaster: "bg-[#4a1414] text-[#ff8f8f]",
  Milestone: "bg-[#382d1a] text-[#FFCF3F]",
};

const CAPE_TIERS = ["Easy", "Medium", "Hard", "Elite", "Master"];

function AssignmentCard({ task, index, onVerify, onSkip, busy, skipLabel, canSkip, hasFreeSkip }) {
  const vtype = task?.meta?.verify?.type;
  const sourceLabel = vtype === "quest" || vtype === "diary" ? "WikiSync" : vtype === "collog_slots" ? "TempleOSRS" : "Hiscores";
  return (
    <div className="osrs-panel-parchment parchment-tex p-4" data-testid={`task-card-${index}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="bg-[#382d1a] text-[#FFCF3F] px-2 py-0.5 border border-black osrs-num text-xs uppercase" data-testid={`task-category-${index}`}>
          {CAT_LABEL[task.category] || task.category}
        </span>
        {task.meta?.tier && (
          <span data-testid={`task-tier-${index}`} className={`px-2 py-0.5 border border-black osrs-num text-xs uppercase ${TIER_COLOR[task.meta.tier] || "bg-[#382d1a] text-[#FFCF3F]"}`}>
            {task.meta.tier}
          </span>
        )}
        <span className="inline-flex items-center gap-1 bg-[#382d1a] text-[#FFCF3F] px-2 py-0.5 border border-black osrs-num text-xs">
          <Coins size={12} /> +{task.meta?.points ?? 5} pts
        </span>
        <span className="inline-flex items-center gap-1 text-[#2f6b1c] text-xs font-bold ml-auto">
          <ShieldCheck size={13} /> {sourceLabel}-verified
        </span>
      </div>
      <p className="font-bold text-[#2a2113] text-lg leading-snug" data-testid={`task-title-${index}`}>{task.title}</p>
      <p className="text-[#4a3c22] text-sm mt-1 leading-snug">{task.description}</p>
      {taskWikiUrl(task) && (
        <a data-testid={`task-wiki-link-${index}`} href={taskWikiUrl(task)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-[#1f5fbf] hover:text-[#0a3f8f] text-xs font-bold underline decoration-dotted underline-offset-2">
          <BookOpen size={13} /> Wiki guide
        </a>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button data-testid={`verify-btn-${index}`} onClick={() => onVerify(task)} disabled={busy}
          className="osrs-btn osrs-btn-green px-4 py-2.5 text-sm flex items-center gap-2">
          <ShieldCheck size={16} /> {busy ? "Checking..." : "Verify & Complete"}
        </button>
        <button data-testid={`skip-btn-${index}`} onClick={() => onSkip(task)} disabled={busy || !canSkip}
          title={hasFreeSkip ? "Free skip" : canSkip ? "Skip this task" : "Not enough points to skip"}
          className={`osrs-btn px-4 py-2.5 text-sm flex items-center gap-2 ${hasFreeSkip ? "osrs-btn-green" : "osrs-btn-red"}`}>
          <SkipForward size={16} /> {skipLabel}
        </button>
      </div>
    </div>
  );
}

export default function SlayerMaster({ profile, onPointsChange, totalPoints, reloadKey }) {
  const { user, refreshUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [slots, setSlots] = useState(1);
  const [skipCost, setSkipCost] = useState(15);
  const [rewardSkip, setRewardSkip] = useState(false);
  const [stats, setStats] = useState({ completed: 0, skipped: 0 });
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [winCard, setWinCard] = useState(null);
  const [capeTiers, setCapeTiers] = useState([]);
  const [blocked, setBlocked] = useState([]);

  const applyBoard = (data) => {
    if (Array.isArray(data.tasks)) setTasks(data.tasks);
    if (typeof data.slots === "number") setSlots(data.slots);
    if (typeof data.skip_cost === "number") setSkipCost(data.skip_cost);
    if (typeof data.reward_skip_available === "boolean") setRewardSkip(data.reward_skip_available);
    setStats({ completed: data.completed ?? 0, skipped: data.skipped ?? 0 });
    if (typeof data.streak === "number") setStreak(data.streak);
    if (Array.isArray(data.cape_tiers)) setCapeTiers(data.cape_tiers);
    if (Array.isArray(data.blocked_categories)) setBlocked(data.blocked_categories);
    onPointsChange?.();
  };

  const reloadCurrent = async () => {
    try {
      const { data } = await api.get(`/profiles/${profile.id}/current-task`);
      applyBoard(data);
      return data.tasks;
    } catch { return null; }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/profiles/${profile.id}/current-task`)
      .then(({ data }) => applyBoard(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, reloadKey]);

  const assign = async () => {
    setBusyId("assign");
    try {
      const { data } = await api.post(`/profiles/${profile.id}/assign-task`);
      applyBoard(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const verify = async (task) => {
    setBusyId(task.id);
    try {
      const { data } = await api.post(`/tasks/${task.id}/verify`);
      toast.success(`${data.message || "Verified!"} +${data.points_awarded} pts${data.bonus ? " 🔥 (2× streak bonus!)" : ""}`);
      const backend = process.env.REACT_APP_BACKEND_URL;
      setWinCard({
        title: task.title,
        url: `${backend}/api/public/task-card.png?rsn=${encodeURIComponent(profile.username)}&task=${encodeURIComponent(task.title)}&pts=${data.points_awarded}`,
      });
      const st = typeof data.streak === "number" ? data.streak : streak;
      const wasFirst = stats.completed === 0;
      applyBoard(data);
      const openShare = () => window.dispatchEvent(new CustomEvent("taskscape:open-share"));
      if (st > 0 && st % 5 === 0) {
        setTimeout(() => toast(`🔥 ${st}-task streak on ${profile.username}! Show off your rank.`, {
          icon: "🏆", duration: 10000, action: { label: "Share rank", onClick: openShare },
        }), 700);
      } else if (wasFirst) {
        setTimeout(() => toast("First task down! Share your rank card to challenge your friends.", {
          icon: "📣", duration: 9000, action: { label: "Share", onClick: openShare },
        }), 700);
      }
    } catch (e) {
      if (e.response?.status === 404) {
        await reloadCurrent();
        toast.info("Task refreshed — try again.");
      } else {
        toast.dismiss();
        toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 6000 });
      }
    } finally {
      setBusyId(null);
    }
  };

  const skip = async (task) => {
    setBusyId(task.id);
    try {
      const { data } = await api.post(`/tasks/${task.id}/skip`);
      toast(data.message, { icon: data.free_skip ? "🎟️" : "⏭️" });
      applyBoard(data);
      if (data.free_skip) refreshUser?.();
    } catch (e) {
      if (e.response?.status === 404) {
        await reloadCurrent();
        toast.info("Task refreshed — try again.");
      } else {
        toast.dismiss();
        toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 6000 });
      }
    } finally {
      setBusyId(null);
    }
  };

  const bonusSkips = (user?.bonus_skips_remaining ?? 0) + (user?.is_supporter ? (user?.free_skips_remaining ?? 0) : 0);
  const hasFreeSkip = bonusSkips > 0 || rewardSkip;
  const canSkip = hasFreeSkip || (totalPoints ?? 0) >= skipCost;
  const skipLabel = bonusSkips > 0
    ? `Skip (free · ${bonusSkips} left)`
    : rewardSkip
      ? "Skip (free · reward)"
      : `Skip (-${skipCost} pts)`;

  return (
    <div className="osrs-panel p-5" data-testid="slayer-master">
      {/* Master header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="skill-node p-1 shrink-0">
          <img src="/taskmaster.png" alt="Task Master" data-testid="task-master-icon"
            className="w-10 h-10 object-contain" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
        </div>
        <div>
          <h2 className="osrs-title text-xl leading-tight">Task Master</h2>
          <p className="osrs-num text-[#00FFFF] osrs-shadow text-sm">
            {stats.completed} completed · {stats.skipped} skipped
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="osrs-shadow text-xs text-[#E6A519] uppercase">Points · This Month</p>
          <p className="osrs-num text-[#FFCF3F] osrs-shadow text-2xl flex items-center gap-1 justify-end" data-testid="slayer-points">
            <Coins size={16} /> {totalPoints ?? 0}
          </p>
          <div className="mt-1 flex justify-end"><TaskHistory profileId={profile.id} reloadKey={`${stats.completed}-${reloadKey}`} /></div>
        </div>
      </div>

      {/* Streak meter */}
      <div className={`flex items-center gap-3 mb-5 px-3 py-2 border-2 border-black ${streak >= 5 ? "bg-[#5a2a10]" : "bg-[#241b10]"}`} data-testid="streak-meter">
        <Flame size={20} className={streak >= 5 ? "text-[#FF7A1A]" : "text-[#7a6a45]"} style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
        {streak >= 5 ? (
          <span className="osrs-shadow text-sm text-[#FF9A3A] font-bold" data-testid="streak-label">
            On fire! Streak {streak} — every task pays <span className="text-[#FFCF3F]">2× points</span>!
          </span>
        ) : (
          <span className="osrs-shadow text-sm text-[#E6A519]" data-testid="streak-label">
            Streak {streak}/5 · complete {5 - streak} more in a row for <span className="text-[#FFCF3F]">2× points</span>
          </span>
        )}
        <div className="ml-auto flex gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={`w-3 h-3 border border-black ${i < Math.min(streak, 5) ? "bg-[#FF7A1A]" : "bg-black/50"}`} />
          ))}
        </div>
      </div>

      {/* TaskScape Cape milestone */}
      <div className="flex items-center gap-3 mb-5 px-3 py-2 border-2 border-black bg-[#241b10]" data-testid="taskscape-cape">
        <Trophy size={20} className={capeTiers.length >= 5 ? "text-[#FFD700]" : "text-[#7a6a45]"} style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
        <div className="flex-1 min-w-0">
          {capeTiers.length >= 5 ? (
            <span className="osrs-shadow text-sm text-[#FFD700] font-bold">TaskScape Cape earned! You've conquered every tier. 👑</span>
          ) : (
            <span className="osrs-shadow text-sm text-[#E6A519]">
              TaskScape Cape · <span className="text-[#FFCF3F]">{capeTiers.length}/5</span> tiers — complete a task of each difficulty
            </span>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {CAPE_TIERS.map((t) => (
            <span key={t} title={t} className={`text-[9px] osrs-num px-1 py-0.5 border border-black ${capeTiers.includes(t) ? (TIER_COLOR[t] || "bg-[#382d1a] text-[#FFCF3F]") : "bg-black/40 text-[#5a4a2a]"}`}>
              {t[0]}
            </span>
          ))}
        </div>
      </div>

      {/* Task slots indicator */}
      <div className="flex items-center gap-2 mb-3" data-testid="task-slots-indicator">
        <LayoutGrid size={15} className="text-[#7CFC7C]" />
        <span className="osrs-shadow text-xs text-[#E6A519]">
          {slots > 1 ? (
            <>Task slots · <span className="osrs-num text-[#FFCF3F]">{tasks.length}/{slots}</span> — juggle {slots} different task types at once</>
          ) : (
            <>Task slot · <span className="osrs-num text-[#FFCF3F]">1</span> — complete tasks to unlock more slots (Rewards)</>
          )}
        </span>
      </div>

      {/* Win card banner */}
      {winCard && (
        <div className="mb-4 bg-[#1c2a16] border-2 border-[#3f6a2a] p-3" data-testid="win-card">
          <div className="flex items-center justify-between mb-2">
            <p className="osrs-title text-sm text-[#00FF00] osrs-shadow flex items-center gap-2">
              <Check size={16} /> Completed: {winCard.title}
            </p>
            <button onClick={() => setWinCard(null)} className="osrs-btn px-2 py-1" data-testid="win-card-dismiss"><X size={13} /></button>
          </div>
          <img src={winCard.url} alt="Task complete card" className="mx-auto max-w-full sm:max-w-md border-2 border-black" loading="lazy" />
          <div className="flex items-center justify-center gap-2 mt-2">
            <a data-testid="win-card-download" href={winCard.url} download={`taskscape-${profile.username}.png`} target="_blank" rel="noreferrer" className="osrs-btn px-4 py-2 text-xs">Download</a>
            <button data-testid="win-card-share" onClick={async () => {
              const text = `I just completed a task on TaskScape! taskscape.quest`;
              try {
                if (navigator.share) { await navigator.share({ title: "TaskScape", text, url: "https://taskscape.quest" }); }
                else { await navigator.clipboard.writeText(`${text}`); toast.success("Link copied — paste it anywhere!"); }
              } catch {}
            }} className="osrs-btn osrs-btn-green px-4 py-2 text-xs">Share this win 📣</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="osrs-shadow text-[#E6A519] text-center py-10">Consulting the Task master...</p>
      ) : tasks.length === 0 ? (
        <div className="text-center py-10" data-testid="no-task">
          <ScrollText size={44} className="mx-auto text-[#5a4a2a] mb-3" />
          <p className="osrs-shadow text-[#E6A519] mb-5">You have no active assignment. Ask the Task master for a task!</p>
          <button data-testid="assign-task-btn" onClick={assign} disabled={busyId === "assign"} className="osrs-btn osrs-btn-green px-6 py-3 text-sm flex items-center gap-2 mx-auto">
            <Sparkles size={16} /> {busyId === "assign" ? "Assigning..." : "Get Tasks"}
          </button>
        </div>
      ) : (
        <div data-testid="current-task" className={slots > 1 ? "grid grid-cols-1 xl:grid-cols-2 gap-3" : "space-y-3"}>
          {tasks.map((t, i) => (
            <AssignmentCard
              key={t.id}
              task={t}
              index={i}
              onVerify={verify}
              onSkip={skip}
              busy={busyId === t.id}
              skipLabel={skipLabel}
              canSkip={canSkip}
              hasFreeSkip={hasFreeSkip}
            />
          ))}
        </div>
      )}

      <BlockTaskTypes
        profileId={profile.id}
        blocked={blocked}
        totalPoints={totalPoints ?? 0}
        f2pOnly={!!profile.f2p_only}
        onChange={(nb) => { setBlocked(nb); onPointsChange?.(); }}
      />
    </div>
  );
}
